import { useState } from "react";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { doc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { db, storage } from "../../lib/firebase";
import styles from "./DocUploader.module.css";

const DOC_TYPES = [
  { value: "datasheet", label: "Ficha técnica" },
  { value: "manual", label: "Manual" },
  { value: "blueprint", label: "Plano" },
  { value: "other", label: "Otro" },
];

/**
 * Sube documentos (PDF/DWG/etc) y los registra dentro de un array `docs`
 * en el documento Firestore del proyecto indicado por `projectId`.
 */
export default function DocUploader({ projectId, docs = [], onChange }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [type, setType] = useState("manual");
  const [label, setLabel] = useState("");
  const [error, setError] = useState(null);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      setError("Documento demasiado grande (máx 25 MB).");
      return;
    }
    if (!label.trim()) {
      setError("Pon un nombre/etiqueta al documento antes de subirlo.");
      return;
    }
    setError(null);
    setBusy(true);
    setProgress(0);

    const safeName = `${Date.now()}-${file.name.replace(/\s+/g, "_")}`;
    const storagePath = `projects/${projectId}/docs/${safeName}`;
    const sRef = ref(storage, storagePath);
    const task = uploadBytesResumable(sRef, file);

    task.on(
      "state_changed",
      (s) =>
        setProgress(Math.round((s.bytesTransferred / s.totalBytes) * 100)),
      (err) => {
        setError(err.message);
        setBusy(false);
      },
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          const entry = {
            label: label.trim(),
            type,
            url,
            storagePath,
            sizeKB: Math.round(file.size / 1024),
          };
          await updateDoc(doc(db, "projects", projectId), {
            docs: arrayUnion(entry),
          });
          onChange?.([...docs, entry]);
          setLabel("");
        } catch (err) {
          setError(err.message);
        } finally {
          setBusy(false);
          setProgress(0);
          e.target.value = "";
        }
      }
    );
  }

  async function handleRemove(entry) {
    if (!confirm(`¿Eliminar "${entry.label}"?`)) return;
    setBusy(true);
    try {
      try {
        await deleteObject(ref(storage, entry.storagePath));
      } catch {
        /* ignorar si no existe */
      }
      await updateDoc(doc(db, "projects", projectId), {
        docs: arrayRemove(entry),
      });
      onChange?.(docs.filter((d) => d.storagePath !== entry.storagePath));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.form}>
        <label className={styles.field}>
          <span>Tipo</span>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            {DOC_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.field} data-grow>
          <span>Etiqueta</span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Ej: Manual de operación · Rev. 02"
          />
        </label>
        <label className={`${styles.upload} ${busy ? styles.busy : ""}`}>
          <input
            type="file"
            accept=".pdf,.dwg,.dxf,.step,.stp,.iges,.igs,.zip"
            onChange={handleFile}
            disabled={busy}
            hidden
          />
          {busy ? `↳ ${progress}%` : "Subir archivo"}
        </label>
      </div>

      {error && <p className={styles.error}>// {error}</p>}

      <ul className={styles.list}>
        {docs.length === 0 && (
          <li className={styles.empty}>// aún no hay documentos</li>
        )}
        {docs.map((d, i) => (
          <li key={i} className={styles.row}>
            <span className={styles.typeTag}>
              {DOC_TYPES.find((t) => t.value === d.type)?.label || "Doc"}
            </span>
            <a
              href={d.url}
              target="_blank"
              rel="noreferrer"
              className={styles.name}
            >
              {d.label}
            </a>
            <span className={styles.size}>{d.sizeKB} KB</span>
            <button
              type="button"
              onClick={() => handleRemove(d)}
              className={styles.del}
            >
              Eliminar
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
